import { ClubReference } from "@/types/club.types";
import { AvatarFields, CountryCode, Id, StorageFile } from "@/types/common.types";
import { ContentLinkVisibility } from "@/types/contentLink.types";
import { Document, DocumentFolder } from "@/types/document.types";
import { FeesSegment, ServiceType, TournatedFeeVat } from "@/types/payment.types";
import { BasePlatform, Platform, PlatformReference } from "@/types/platform.types";
import { SportRef } from "@/types/sport.types";

export const OrganizationStatusValues = ["active", "inactive"] as const;
export type OrganizationStatus = (typeof OrganizationStatusValues)[number];

export const OrganizationTypeValues = [
  "governmentAuthority",
  "umbrellaSportsBody",
  "nationalGoverningBody",
  "regionalGoverningBody",
  "internationalGoverningBody",
  "municipality",
  "clubOwner",
  "clubChainOwner",
  "privateEntity",
  "sportsClub",
  "school",
  "union",
  "sportsMinistry",
  "community",
  "educationalInstitution",
] as const;

export type OrganizationType = (typeof OrganizationTypeValues)[number];

export const OrganizationGeoScopeValues = ["international", "national", "local"] as const;
export type OrganizationGeoScope = (typeof OrganizationGeoScopeValues)[number];

export const OrganizationSubscriptionValues = [
  "custom",
  "customPlus",
  "customUltra",
  "marketplace",
] as const;

export type OrganizationSubscription = (typeof OrganizationSubscriptionValues)[number];

export type OrganizationReference = {
  id: string;
  name: string;
  abbreviation: string | null;
  logo: string | null;
};

export type Organization = {
  id: string;
  types: OrganizationType[];
  name: string;
  abbreviation: string;
  about: string | null;
  educationalInstitutionNumber: string | null;
  geoScope: OrganizationGeoScope;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  logoFile: StorageFile | null;
  legacyLogoUrl: string | null;
  country: CountryCode | null;
  status: OrganizationStatus;
  enableLicenses: boolean;
  enableCertificates: boolean;
  enableApplicationForms: boolean;
  enableRankings: boolean;
  isClubForTeamRequired: boolean;
  /**
   * Whether other entities may tag their news onto this organization, and the
   * visibility a fresh news tag gets (Extras tab, see docs/content-tagging.md).
   */
  allowNewsTagging: boolean;
  defaultTaggedNewsVisibility: ContentLinkVisibility;
  /** The same pair for galleries (albums), toggled independently of news. */
  allowGalleryTagging: boolean;
  defaultTaggedGalleryVisibility: ContentLinkVisibility;
  createdAt: Date;
  updatedAt: Date;
  sports: SportRef[];
};

export type OrganizationWithRelationalId = {
  relationalId: Id;
};

/**
 * Slim manager projection the federation REST endpoints return — only the
 * contact fields the list/detail UIs actually render. Not the full domain `User`.
 */
export type OrganizationManager = AvatarFields & {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone: string | null;
  /** Public contact email of the user (`user.contactEmail`), separate from the login `email`. Null when unset. */
  contactEmail: string | null;
  /** Public contact phone of the user (`user.contactPhone`, E.164), separate from `phone`. Null when unset. */
  contactPhone: string | null;
};

export type OrganizationWithManagers = {
  mainManager: OrganizationManager;
  mainManagerContactEmail: string | null;
  mainManagerContactPhone: string | null;
  managers: OrganizationManager[];
};

export type OrganizationWithPlatforms<TPlatform extends { id: string } = BasePlatform> = {
  platforms: TPlatform[];
};

/** Shape of a row in the organizations list table (REST `federationList`). */
export type OrganizationListItem = {
  id: string;
  /**
   * Temporary numeric DB id bridging the public-facing organization URL
   * (`{clientUrl}/organization/{internalId}`) that still takes the numeric id.
   * Deprecated on the API — do not use for anything but the public-view link.
   */
  internalId: number;
  types: OrganizationType[];
  name: string;
  abbreviation: string;
  about: string | null;
  educationalInstitutionNumber: string | null;
  geoScope: OrganizationGeoScope;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  logoFile: StorageFile | null;
  legacyLogoUrl: string | null;
  country: CountryCode | null;
  status: OrganizationStatus;
  enableLicenses: boolean;
  enableCertificates: boolean;
  enableApplicationForms: boolean;
  enableRankings: boolean;
  isClubForTeamRequired: boolean;
  allowNewsTagging: boolean;
  defaultTaggedNewsVisibility: ContentLinkVisibility;
  allowGalleryTagging: boolean;
  defaultTaggedGalleryVisibility: ContentLinkVisibility;
  createdAt: Date;
  updatedAt: Date;
  sports: SportRef[];
  platforms: PlatformReference[];
  mainManager: OrganizationManager;
  mainManagerContactEmail: string | null;
  mainManagerContactPhone: string | null;
  managers: OrganizationManager[];
};

export type OrganizationWithFees = {
  customFees: {
    [platformId in string]?:
      | {
          [key in ServiceType]: FeesSegment[] | null;
        }
      | null;
  };
  /**
   * The entity's OWN VAT on the Tournated fee per platform public id. A platform
   * absent from the map inherits the platform's VAT.
   */
  customFeeVat: { [platformId in string]?: TournatedFeeVat };
};

export type OrganizationWithLegalDetails = {
  legalDetails: OrganizationLegalDetails;
};

export type OrganizationWithPaymentDetails = {
  paymentDetails: OrganizationPaymentDetails;
};

export type OrganizationWithClubMembers = {
  clubMembers: OrganizationClubMember[];
};

export type OrganizationWithBoardMembers = {
  boardMembers: BoardMember[];
};

export type OrganizationWithDocuments = {
  documents: OrganizationDocument[];
  documentFolders: OrganizationDocumentFolder[];
};

// Documents/folders are a shared domain (see Document/DocumentFolder); organizations
// expose them under their own names for the org mappers and `OrganizationWithDocuments`.
export type OrganizationDocument = Document;
export type OrganizationDocumentFolder = DocumentFolder;

// Social/additional links are an entity-agnostic domain (see links.types). They
// were first introduced on organizations; the org-named aliases below keep the
// existing org mappers/forms reading naturally while the canonical types live in
// the shared module (also consumed by leagues).
export type {
  SocialLink as OrganizationSocialLink,
  AdditionalLink as OrganizationAdditionalLink,
  EntityWithLinks as OrganizationWithLinks,
} from "./links.types";

export const LegalEntityRoleValues = [
  "president",
  "vicePresident",
  "boardMember",
  "secretaryGeneral",
  "chiefExecutiveOfficer",
  "treasurer",
  "executiveDirector",
  "technicalDirector",
  "operationsManager",
  "competitionManager",
  "committeeChair",
  "committeeMember",
  "athleteRepresentative",
  "clubRepresentative",
  "generalAssemblyMember",
  "votingMember",
  "officiatingManager",
  "coachingManager",
] as const;

export type LegalEntityRole = (typeof LegalEntityRoleValues)[number];

export type OrganizationPlatformRelation = {
  id: number;
  federation: Organization | null;
  platform: Platform | null;
};

export type BoardMember = {
  id: number;
  name: string;
  surname: string;
  role: LegalEntityRole;
  email: string;
  phone: string | null;
  logoUrl: {
    url: string;
    fileId: string;
  } | null;
};

export type OrganizationClubMember = {
  id: number;
  title: string;
  contactName: string;
  contactSurname: string;
  contactEmail: string;
  contactPhone: string | null;
  contactRole: LegalEntityRole;
  logoUrl: string | null;
  registrationNumber: string;
  website: string | null;
  country: CountryCode | null;
  city: string;
  address: string;
  postalCode: string;
  club: ClubReference | null;
};

export type OrganizationIndividualLegalDetails = {
  type: "individual";
  name: string;
  surname: string;
  residence: CountryCode | null;
  personalNumber: string | null;
  email: string;
  phone: string | null;
};

export type OrganizationCompanyLegalDetails = {
  /** Public legal contact email — shown in the default legal documents. */
  email: string;
  type: "company";
  companyName: string;
  registrationNumber: string;
  registrationCountry: CountryCode | null;
  vatNumber: string;
  address: string;
  signerName: string;
  signerSurname: string;
  signerRole: string;
  signerEmail: string;
};

export type OrganizationLegalDetails =
  | OrganizationIndividualLegalDetails
  | OrganizationCompanyLegalDetails;

export type CommonOrgBankTransferDetails = {
  enabled: boolean;
  country: CountryCode;
  city: string;
  address: string;
  postalCode: string;
};

export type IndividualOrgBankTransferDetails = CommonOrgBankTransferDetails & {
  type: "individual";
  accountHolderName: string;
  iban: string;
};

export type CompanyOrgBankTransferDetails = CommonOrgBankTransferDetails & {
  type: "company";
  accountHolderName: string;
  companyRegistrationNumber: string;
  vatNumber: string;
  bankName: string;
  iban: string;
};

export type OrgBankTransferDetails =
  | IndividualOrgBankTransferDetails
  | CompanyOrgBankTransferDetails;

export type OrganizationPaymentDetails = {
  bankTransferDetails: OrgBankTransferDetails;
};

export const OrganizationsTableColumnValues = [
  "id",
  "name",
  "type",
  "sports",
  "owner",
  "platforms",
  "subscription",
  "athletes",
  "entries",
  "revenueTransactions",
  "createdAt",
  "status",
] as const;

export type OrganizationsTableColumn = (typeof OrganizationsTableColumnValues)[number];
